import { classes } from 'html-classes';
import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import type { CartCalculationOrderResponse } from '~/api/Catalog';
import { PaymentSystem } from '~/api/Order';
import CloudflareResponseImage from '~/components/CloudflareResponsiveImage/CloudflareResponseImage';
import { defaultSrcSetParams } from '~/components/CloudflareResponsiveImage/constants';
import FreeDeliveryProgressBar from '~/components/FreeDeliveryProgressBar/FreeDeliveryProgressBar';
import Icon from '~/components/Icon/Icon';
import {
  Carousel,
  CarouselContent,
  CarouselItem,
} from '~/components/isolated/Carousel';
import { useCategoryAnalytics } from '~/hooks/useCategoryAnalytics';
import adyenService from '~/services/Adyen';
import { checkoutStore } from '~/stores';
import type Delivery from '~/stores/CheckoutStore/Delivery';
import { isProductBundle } from '~/types/Product';
import styles from './DeliveryMobile.module.scss';
import { deliveryCarouselOpts } from './deliveryCarouselConfig';
import { useDelivery } from './useDelivery';

interface DeliveryMobileProps {
  handleSelectDateModal: () => void;
  delivery: Delivery;
  slotNumber?: number;
  order: CartCalculationOrderResponse;
}

const DeliveryMobile = ({
  handleSelectDateModal,
  delivery,
  slotNumber,
  order,
}: DeliveryMobileProps) => {
  const { t } = useTranslation();
  const { handleClickProduct } = useCategoryAnalytics();

  const {
    deliveryItems,
    showSelectTimeButton,
    deliveryPrice,
    isDeliveryFree,
    leftUntilFreeDelivery,
    progressBarWidth,
    getSelectedDayAndTime,
    openDeliveryModal,
    deliveryTooltip: showDeliveryTooltip,
  } = useDelivery({ delivery, order });

  const { day: selectedDay, time: selectedTime } = getSelectedDayAndTime();

  const deliveryTooltip = showDeliveryTooltip && (
    <div onClick={openDeliveryModal} className={styles.deliveryTooltip}>
      <Icon type="info" size={16} />
    </div>
  );
  return (
    <li className={classes(['slot-list__slot', styles.container])}>
      <div className={styles.content}>
        <div className={styles.contentInfo}>
          <div className={styles.contentHeader}>
            {!slotNumber ? (
              <p className={styles.slotType}>
                <span className={styles.slotTypeDay}>
                  <span className={styles.slotTypeLine}>
                    {t('expressDelivery')}
                  </span>
                  {deliveryTooltip}
                </span>
                <span className={styles.slotTypeLine}>{selectedDay}</span>
              </p>
            ) : (
              <p className={styles.slotType}>
                <span className={styles.slotTypeDay}>
                  <span className={styles.slotTypeLine}>{selectedDay}</span>
                  {deliveryTooltip}
                </span>
                <span className={styles.slotTypeLine}>{selectedTime}</span>
              </p>
            )}
            {isDeliveryFree ? (
              <p className={styles.deliveryFree}>{t('deliveryFree')}</p>
            ) : (
              <p className="slot-list__slot-fee">
                {t('deliveryFee')}: {deliveryPrice}
              </p>
            )}
          </div>
          {showSelectTimeButton && (
            <button
              className={styles.selectTimeBtn}
              onClick={handleSelectDateModal}
              data-button="select-date"
              disabled={
                checkoutStore.activePaymentSystem === PaymentSystem.adyen &&
                adyenService.step === 2
              }
            >
              {t('selectTime')}
            </button>
          )}
        </div>
        <ul className="slot-list__slot-images">
          <Carousel opts={deliveryCarouselOpts}>
            <CarouselContent>
              {deliveryItems.map((item) => (
                <CarouselItem
                  className={styles.slide}
                  key={item.previewImageThumb}
                >
                  <Link
                    onClick={handleClickProduct(item)}
                    to={
                      `/p/${item.slug}` +
                      (isProductBundle(item) ? '' : `?sku=${item.sku}`)
                    }
                    state={{
                      ...(isProductBundle(item) ? {} : { offerSku: item.sku }),
                    }}
                  >
                    <div className="slot-list__slot-images-item">
                      <CloudflareResponseImage
                        src={item.previewImageThumb || ''}
                        srcParams={{ width: 80, height: 110 }}
                        srcSetParams={defaultSrcSetParams}
                        alt={item.name}
                        loading="lazy"
                      />
                    </div>
                  </Link>
                </CarouselItem>
              ))}
            </CarouselContent>
          </Carousel>
        </ul>
        {!isDeliveryFree && (
          <FreeDeliveryProgressBar
            leftUntilFreeDelivery={leftUntilFreeDelivery}
            cssWidth={progressBarWidth}
          />
        )}
      </div>
    </li>
  );
};

export default observer(DeliveryMobile);
